/**
 * Calendar Requests Page
 * Manage scheduled contacts/calls with organizations
 */

import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getCalendarRequests,
  createCalendarRequest,
  updateCalendarRequest,
  deleteCalendarRequest,
} from "../services/calendarRequests.service";
import {
  QUERY_KEYS,
  DEFAULT_PAGE_SIZE,
  TIMEZONE_OPTIONS,
} from "../utils/constants";
import { formatDate } from "../utils/formatters";
import { supabase } from "../services/supabase";
import { fetchAllRows } from "../utils/fetchAllRows";
import type {
  CalendarRequest,
  CalendarRequestStatus,
  CalendarRequestFilters,
  CreateCalendarRequestDTO,
  UpdateCalendarRequestDTO,
} from "../types/calendarRequest.types";
import type { UUID } from "../types/common.types";
import CalendarRequestFormModal from "../components/calendarRequests/CalendarRequestFormModal";

const STATUS_CONFIG: Record<
  CalendarRequestStatus,
  { label: string; color: string; bg: string }
> = {
  requested: {
    label: "Requested",
    color: "text-blue-800",
    bg: "bg-blue-100",
  },
  scheduled: {
    label: "Scheduled",
    color: "text-purple-800",
    bg: "bg-purple-100",
  },
  not_done: {
    label: "Not Done",
    color: "text-red-800",
    bg: "bg-red-100",
  },
  postponed: {
    label: "Postponed",
    color: "text-yellow-800",
    bg: "bg-yellow-100",
  },
  completed: {
    label: "Completed",
    color: "text-green-800",
    bg: "bg-green-100",
  },
};

const CALENDAR_REQUEST_STATUSES: {
  value: CalendarRequestStatus | "all";
  label: string;
}[] = [
  { value: "all", label: "All Statuses" },
  { value: "requested", label: "Requested" },
  { value: "scheduled", label: "Scheduled" },
  { value: "not_done", label: "Not Done" },
  { value: "postponed", label: "Postponed" },
  { value: "completed", label: "Completed" },
];

interface Profile {
  id: UUID;
  full_name: string;
}

interface Organization {
  id: UUID;
  name: string;
}

const CalendarRequests: React.FC = () => {
  const queryClient = useQueryClient();

  // Filters
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    CalendarRequestStatus | "all"
  >("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orgFilter, setOrgFilter] = useState<UUID | "all">("all");
  const [assignedFilter, setAssignedFilter] = useState<UUID | "all">("all");

  // Modal state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<CalendarRequest | null>(
    null,
  );
  const [deleteConfirmId, setDeleteConfirmId] = useState<UUID | null>(null);

  // Reference data
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);

  // Load reference data
  useEffect(() => {
    const fetchData = async () => {
      // Page through ALL orgs so the filter dropdown doesn't truncate
      // alphabetically — a single .limit() cannot exceed PostgREST's
      // 1000-row cap. See fetchAllRows.
      const [profilesRes, orgs] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, full_name")
          .neq("role", "readonly") // never an assignee (owner 2026-11-03)
          .order("full_name"),
        // Tolerate an org-fetch failure: log and fall back to an empty list so
        // the rest of the reference data (profiles) still loads, matching the
        // prior behavior where an org error was non-fatal.
        fetchAllRows<Organization>((from, to) =>
          supabase
            .from("organizations")
            .select("id, name")
            .eq("is_archived", false)
            .order("name")
            .range(from, to),
        ).catch((e) => {
          console.error("Error loading organizations:", e);
          return [] as Organization[];
        }),
      ]);

      if (profilesRes.data) {
        setProfiles(profilesRes.data);
      }
      setOrganizations(orgs);
    };

    fetchData();
  }, []);

  // Build filters
  const filters: CalendarRequestFilters = {};
  if (statusFilter !== "all") {
    filters.status = statusFilter;
  }
  if (dateFrom) {
    filters.date_from = dateFrom;
  }
  if (dateTo) {
    filters.date_to = dateTo;
  }
  if (orgFilter !== "all") {
    filters.organization_id = orgFilter;
  }
  if (assignedFilter !== "all") {
    filters.assigned_to = assignedFilter;
  }

  // Fetch data
  const { data, isLoading, error } = useQuery({
    queryKey: [
      QUERY_KEYS.CALENDAR_REQUESTS,
      page,
      statusFilter,
      dateFrom,
      dateTo,
      orgFilter,
      assignedFilter,
    ],
    queryFn: () =>
      getCalendarRequests(
        {
          page,
          limit: DEFAULT_PAGE_SIZE,
          sortBy: "request_date",
          sortOrder: "asc",
        },
        Object.keys(filters).length > 0 ? filters : undefined,
      ),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (dto: CreateCalendarRequestDTO) => createCalendarRequest(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CALENDAR_REQUESTS],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: UUID; dto: UpdateCalendarRequestDTO }) =>
      updateCalendarRequest(id, dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CALENDAR_REQUESTS],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCalendarRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CALENDAR_REQUESTS],
      });
      setDeleteConfirmId(null);
    },
  });

  const handleCreateSave = async (
    input: CreateCalendarRequestDTO | UpdateCalendarRequestDTO,
  ) => {
    await createMutation.mutateAsync(input as CreateCalendarRequestDTO);
  };

  const handleEditSave = async (
    input: CreateCalendarRequestDTO | UpdateCalendarRequestDTO,
  ) => {
    if (!editingRequest) {
      return;
    }
    await updateMutation.mutateAsync({
      id: editingRequest.id,
      dto: input as UpdateCalendarRequestDTO,
    });
    setEditingRequest(null);
  };

  const handleStatusChange = async (
    request: CalendarRequest,
    newStatus: CalendarRequestStatus,
  ) => {
    await updateMutation.mutateAsync({
      id: request.id,
      dto: { status: newStatus },
    });
  };

  const formatTime = (time?: string, tz?: string) => {
    if (!time) {
      return "";
    }
    const tzLabel =
      TIMEZONE_OPTIONS.find((t) => t.value === tz)?.label || tz || "";
    return `${time}${tzLabel ? ` ${tzLabel}` : ""}`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Calendar Requests
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track and manage scheduled contacts with organizations
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          New Request
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              From Date
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              To Date
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(
                  e.target.value as CalendarRequestStatus | "all",
                );
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {CALENDAR_REQUEST_STATUSES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Organization
            </label>
            <select
              value={orgFilter}
              onChange={(e) => {
                setOrgFilter(e.target.value as UUID | "all");
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Organizations</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              Assigned To
            </label>
            <select
              value={assignedFilter}
              onChange={(e) => {
                setAssignedFilter(e.target.value as UUID | "all");
                setPage(1);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Users</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.full_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <svg
              className="animate-spin h-8 w-8 text-primary-600"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          </div>
        ) : error ? (
          <div className="p-6 text-center text-red-600">
            Error loading calendar requests. Please try again.
          </div>
        ) : !data || data.data.length === 0 ? (
          <div className="p-12 text-center">
            <svg
              className="w-12 h-12 mx-auto text-gray-300 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"
              />
            </svg>
            <p className="text-gray-500 text-sm">No calendar requests found</p>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="mt-4 text-primary-600 hover:text-primary-800 text-sm font-medium"
            >
              Create your first request
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assigned To
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.data.map((request) => {
                    const statusConfig =
                      STATUS_CONFIG[request.status] || STATUS_CONFIG.requested;
                    return (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(request.request_date)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {formatTime(request.request_time, request.timezone)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">
                          {request.description}
                          {request.contact_name && (
                            <span className="block text-xs text-gray-500">
                              Contact: {request.contact_name}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {request.organization_name || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                          {request.assigned_to_name || "Unassigned"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <select
                            value={request.status}
                            onChange={(e) =>
                              handleStatusChange(
                                request,
                                e.target.value as CalendarRequestStatus,
                              )
                            }
                            className={`text-xs font-medium px-2 py-1 rounded-full border-0 ${statusConfig.bg} ${statusConfig.color} cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500`}
                          >
                            {CALENDAR_REQUEST_STATUSES.filter(
                              (s) => s.value !== "all",
                            ).map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-sm">
                          <button
                            onClick={() => setEditingRequest(request)}
                            className="text-primary-600 hover:text-primary-800 mr-3"
                            title="Edit"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              />
                            </svg>
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(request.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <p className="text-sm text-gray-500">
                  Showing {(page - 1) * DEFAULT_PAGE_SIZE + 1} to{" "}
                  {Math.min(page * DEFAULT_PAGE_SIZE, data.total)} of{" "}
                  {data.total} results
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() =>
                      setPage((p) => Math.min(data.totalPages, p + 1))
                    }
                    disabled={page === data.totalPages}
                    className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create Modal */}
      <CalendarRequestFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateSave}
      />

      {/* Edit Modal */}
      <CalendarRequestFormModal
        isOpen={!!editingRequest}
        onClose={() => setEditingRequest(null)}
        onSave={handleEditSave}
        request={editingRequest}
      />

      {/* Delete Confirmation */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={() => setDeleteConfirmId(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Delete Request
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                Are you sure you want to delete this calendar request? This
                action cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteMutation.mutate(deleteConfirmId)}
                  disabled={deleteMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarRequests;
